import "./DaResetPassword.css";

import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DaPageWrapper from "../DaPageWrapper/DaPageWrapper";
import { Button } from "@mui/material";
import { Translate } from "../../helpers/TranslatedComponent";
import { T_KEYS } from "../../locales/translationKeys";
import { translateStatic } from "../../hooks/useT/useT";
import { useAppTheme } from "../../theme/useTheme";
import { CredentialMgmtDataService } from "../../services/CredentialMgmtDataService";
import { DaResetLinkExpired } from "../DaResetLinkExpired/DaResetLinkExpired";
import DaShimmer from "../DaShimmer/DaShimmer";

const REGEX = {
  special: /[^A-Za-z0-9]/,
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  number: /[0-9]/,
};

const DaResetPassword = () => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTokenValidated, setIsTokenValidated] = useState(false);
  const [validStatus, setValidStatus] = useState(null);
  const theme = useAppTheme();
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const credentialService = CredentialMgmtDataService();
  const queryParams = new URLSearchParams(search);
  const token = queryParams.get("token");
  const brandId = queryParams.get("brand_id");
  const lang = queryParams.get("lang");
  const clientId = queryParams.get("client_id");

  const buildPathWithQuery = (basePath, clientIdOverride) => {
    const requestQueryParams = new URLSearchParams();
    if (lang) requestQueryParams.set("lang", lang);
    if (brandId) requestQueryParams.set("brand_id", brandId);
    const finalClientId = clientIdOverride ?? clientId;
    if (finalClientId) requestQueryParams.set("client_id", finalClientId);
    return requestQueryParams.toString() ? `${basePath}?${requestQueryParams.toString()}` : basePath;
  };

  useEffect(() => {
    let isMounted = true;

    if (!token) {
      setIsTokenValidated(false);
      setValidStatus({
        status: 400,
        step: "pre-validation",
      });
      return () => {
        isMounted = false;
      };
    }

    credentialService
      .validateToken("/accounts/operations/pre-validate", { token })
      .then(() => {
        if (!isMounted) return;
        setIsTokenValidated(true);
        setValidStatus({ status: 200, step: "pre-validation" });
      })
      .catch((error) => {
        if (!isMounted) return;
        const status = error?.response?.status;

        if (status === 400) {
          setIsTokenValidated(false);
          setValidStatus({ status: 400, step: "pre-validation" });
          return;
        }

        setIsTokenValidated(false);
        setValidStatus({
          status: status ?? 500,
          step: "pre-validation",
        });
      });

    return () => {
      isMounted = false;
    };
  }, [token, brandId]);

  useEffect(() => {
    if (validStatus?.status !== 400) return;
    navigate(buildPathWithQuery(`/ciam/v1/${tenant}/accounts/da-reset-link-expired`), { replace: true });
  }, [validStatus?.status, tenant, navigate]);

  const handleChange = (e) => {
    setPassword(e.target.value);
  };

  const toggleShowPassword = () => setShowPassword((s) => !s);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!allPasswordChecksMet || isSubmitting || !isTokenValidated) {
      setAttemptedSubmit(true);
      return;
    }

    setAttemptedSubmit(false);
    setIsSubmitting(true);
    const changePasswordPayload = {
      password,
      confirmedPassword: password,
      token,
    };
    const changePasswordResource = buildPathWithQuery("/accounts/operations/change-password");

    credentialService
      .activateUser(changePasswordResource, changePasswordPayload)
      .then((response) => {
        setPasswordError("");
        const redirectUrl = response?.data?.redirectUrl;
        const email = response?.data?.email;

        const passwordSavedParams = new URLSearchParams();
        if (lang) passwordSavedParams.set("lang", lang);
        if (brandId) passwordSavedParams.set("brand_id", brandId);
        if (redirectUrl) passwordSavedParams.set("redirectUrl", redirectUrl);
        if (email) passwordSavedParams.set("email", email);

        const queryString = passwordSavedParams.toString();
        const navigationPath = queryString
          ? `/ciam/v1/${tenant}/accounts/da-password-saved?${queryString}`
          : `/ciam/v1/${tenant}/accounts/da-password-saved`;

        navigate(navigationPath);
      })
      .catch((error) => {
        const status = error?.response?.status;
        const errorMessages = error?.response?.data?.error_validations?.map((obj) => obj.message?.toLowerCase()) ?? [];
        const hasInvalidTokenError = errorMessages.includes("invalid token");

        if (status === 409) {
          setAttemptedSubmit(true);
          setPasswordError(translateStatic(T_KEYS.DA.PASSWORD.VALIDATION.PASSWORD_REUSE));
          return;
        }

        if (status === 400 && hasInvalidTokenError) {
          setValidStatus({ status: 400, step: "activation" });
          setAttemptedSubmit(true);
          return;
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const getPasswordRequirements = (pw) => ({
    minLength: pw.length >= 10,
    specialChar: REGEX.special.test(pw),
    uppercase: REGEX.uppercase.test(pw),
    lowercase: REGEX.lowercase.test(pw),
    number: REGEX.number.test(pw),
  });

  const passwordChecks = useMemo(() => getPasswordRequirements(password), [password]);
  const allPasswordChecksMet =
    passwordChecks.minLength &&
    passwordChecks.number &&
    passwordChecks.specialChar &&
    passwordChecks.uppercase &&
    passwordChecks.lowercase;

  const inputClass = [
    "da-password-input",
    attemptedSubmit && (!allPasswordChecksMet || passwordError) ? "error" : "",
    allPasswordChecksMet ? "valid" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderRequirement = (key, text, condition) => (
    <li key={key} className={`validation-messages ${condition ? "req-met" : "req-not-met"}`}>
      {condition ? (
        <img src="/ciam/v1/static/images/da/check-mark.svg" alt="" aria-hidden="true" />
      ) : attemptedSubmit ? (
        <img src="/ciam/v1/static/images/da/minus-icon.svg" alt="" aria-hidden="true" />
      ) : (
        <img src="/ciam/v1/static/images/da/empty-box.svg" alt="" aria-hidden="true" />
      )}
      {text}
    </li>
  );

  if (!validStatus || isSubmitting) {
    return <DaShimmer />;
  }

  if (validStatus?.status === 400) {
    return <DaResetLinkExpired />;
  }

  return (
    <DaPageWrapper
      wrapperClass={"da-resetpassword-card da-page-wrapper"}
      iconType="lock_open"
      title={<Translate tKey={T_KEYS.DA.RESET.TITLE} />}
      subtitle={<Translate tKey={T_KEYS.DA.PASSWORD.SUBTITLE} />}
    >
      <form className="da-resetpassword-form" onSubmit={handleSubmit}>
        <label htmlFor="da-reset-password" className="da-resetpassword-label">
          <Translate tKey={T_KEYS.DA.PASSWORD.LABEL.PASSWORD} />
        </label>

        <div className="da-password-wrapper">
          <input
            id="da-reset-password"
            name="resetpassword"
            className={inputClass}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={handleChange}
            autoFocus
            autoComplete="new-password"
            aria-invalid={attemptedSubmit && !allPasswordChecksMet}
            aria-describedby="password-requirements password-error"
          />

          <button
            type="button"
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="da-password-toggle"
            onClick={toggleShowPassword}
          >
            {showPassword ? (
              theme?.assets?.passwordHiddenIcon ? (
                <img
                  src={theme.assets.passwordHiddenIcon}
                  alt="Hide password"
                  className="da-password-visibility-icon"
                />
              ) : (
                <img
                  src="/ciam/v1/static/images/da/visibility.svg"
                  alt="Hide password"
                  className="da-password-visibility-icon"
                />
              )
            ) : theme?.assets?.passwordVisibleIcon ? (
              <img src={theme.assets.passwordVisibleIcon} alt="Show password" className="da-password-visibility-icon" />
            ) : (
              <img
                src="/ciam/v1/static/images/da/visibility_off.svg"
                alt="Show password"
                className="da-password-visibility-icon"
              />
            )}
          </button>
        </div>

        {attemptedSubmit && (!allPasswordChecksMet || passwordError) && (
          <p className="da-password-validation">
            <img
              src="/ciam/v1/static/images/da/error.svg"
              alt=""
              aria-hidden="true"
              className="da-password-exclamation"
            />
            <span className="da-password-error-text">
              {passwordError || translateStatic(T_KEYS.DA.PASSWORD.VALIDATION.PASSWORD)}
            </span>
          </p>
        )}

        <ul
          id="password-requirements"
          className="da-password-reqs"
          aria-live="polite"
          aria-label="Password requirements"
        >
          {renderRequirement("length", translateStatic(T_KEYS.DA.PASSWORD.REQ.LENGTH), passwordChecks.minLength)}
          {renderRequirement("number", translateStatic(T_KEYS.DA.PASSWORD.REQ.NUMBER), passwordChecks.number)}
          {renderRequirement("special", translateStatic(T_KEYS.DA.PASSWORD.REQ.SPECIAL), passwordChecks.specialChar)}
          {renderRequirement(
            "case",
            translateStatic(T_KEYS.DA.PASSWORD.REQ.CASE),
            passwordChecks.uppercase && passwordChecks.lowercase
          )}
        </ul>
        <Button
          className="da-button da-password-btn"
          type="submit"
          variant="contained"
          disableElevation
          disableRipple
          disableFocusRipple
          aria-disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {translateStatic(T_KEYS.DA.PASSWORD.BUTTON.CONTINUE)}
        </Button>
      </form>
    </DaPageWrapper>
  );
};

export { DaResetPassword };
